/**
 * Lesson 1: What is a Prediction Market?
 *
 * Introduces the core concept of prediction markets as information aggregators,
 * and explains that prices represent probabilities.
 */

import type { Lesson } from '../manager'

export const lesson1: Lesson = {
  id: 'lesson-1',
  title: 'What is a Prediction Market?',
  description: 'Learn how prediction markets work and why prices are rough probability estimates',
  order: 1,
  prerequisites: [],
  completionCriteria: { type: 'quiz-pass', minQuizScore: 70 },
  objectives: [
    'Understand what a prediction market is',
    'Learn how markets aggregate information',
    'Recognize that prices are rough probability estimates',
  ],
  content: [
    // Page 1: Introduction
    {
      type: 'text',
      data: {
        markdown: `# What is a Prediction Market?

Imagine you want to know the probability of something happening—like whether it will rain tomorrow, or who will win an election. You could ask experts, but they often disagree. You could take a poll, but people might not tell the truth.

**Prediction markets** offer a clever solution: let people **put money on the line**. When real stakes are involved, people are more likely to reveal what they actually believe.

## How It Works

In a prediction market:
- You buy **shares** that pay out if an event happens
- The **price** of a share reflects the market's belief about the probability
- If you think the market is wrong, you can profit by trading

For example, if shares for "Team A wins" cost **$0.70**, the market is saying the event has about a **70% chance** of happening in this simplified binary model.`,
        visualizationId: 'probability-display',
      },
    },

    // Page 2: Markets as Information Aggregators
    {
      type: 'text',
      data: {
        markdown: `## Markets Aggregate Information

One person might know a little about the weather. Another knows about the teams' recent performance. A third follows injury reports closely.

In a prediction market, all these people trade based on their knowledge. The result? The **price incorporates everyone's information**.

This is called the **wisdom of crowds**. Research shows prediction markets can sometimes outperform experts or polls, especially when information is diverse and incentives are strong.

> **Key Insight**: The market price is like a "collective brain" that combines many individual opinions into a single number. It continuously updates as beliefs change.

## Why Trust the Price?

If someone thinks the market is wrong, they have an incentive to trade and profit. This constant buying and selling pushes the price toward a better estimate. Disagreement is not a problem—it is the mechanism that improves the forecast.`,
      },
    },

    // Page 3: Price = Probability
    {
      type: 'text',
      data: {
        markdown: `## Price ≈ Probability

This is the most important concept in prediction markets:

**In this simplified binary model, the price of a share is a rough estimate of the probability.**

| Price | Probability | Meaning |
|-------|-------------|---------|
| $0.10 | 10% | Very unlikely to happen |
| $0.50 | 50% | Could go either way |
| $0.70 | 70% | Likely to happen |
| $0.90 | 90% | Very likely to happen |

### Example

If "Will it rain tomorrow?" shares trade at **$0.65**, the market believes there's a **65% chance of rain**.

- If it **does rain**, each share pays **$1.00** (you profit $0.35)
- If it **doesn't rain**, each share pays **$0.00** (you lose $0.65)

The market price is a useful probability estimate—it continuously updates as beliefs change.`,
        visualizationId: 'probability-display',
      },
    },

    // Quiz 1
    {
      type: 'quiz',
      data: {
        question: 'In a prediction market, what does a share price of $0.80 mean?',
        options: [
          { id: 'a', text: 'The share costs 80 cents to buy' },
          { id: 'b', text: 'The market estimates an 80% probability the event will happen' },
          { id: 'c', text: 'You will make 80 cents profit if you buy' },
          { id: 'd', text: 'Both A and B are correct' },
        ],
        correctOptionId: 'd',
        explanation:
          'Correct! A price of $0.80 means both that you pay 80 cents per share AND that the market estimates about an 80% probability. In this simplified binary model, the price is a rough probability estimate.',
      },
    },

    // Quiz 2
    {
      type: 'quiz',
      data: {
        question: 'Why are prediction markets often accurate?',
        options: [
          { id: 'a', text: 'They use special AI algorithms' },
          { id: 'b', text: 'People with different knowledge trade and the price reflects all their information' },
          { id: 'c', text: 'Only experts are allowed to trade' },
          { id: 'd', text: 'The government regulates the prices' },
        ],
        correctOptionId: 'b',
        explanation:
          'Prediction markets aggregate information from many traders with different knowledge. Each trader who spots mispricing has an incentive to trade, which pushes the price toward a better estimate. This "wisdom of crowds" effect is what makes prediction markets useful.',
      },
    },

    // Quiz 3
    {
      type: 'quiz',
      data: {
        question: 'If shares for "Candidate X wins" are priced at $0.35, what does this tell you?',
        options: [
          { id: 'a', text: 'Candidate X is the favorite to win' },
          { id: 'b', text: 'The market thinks Candidate X has about a 35% chance of winning' },
          { id: 'c', text: 'You should definitely buy these shares' },
          { id: 'd', text: 'The election is rigged' },
        ],
        correctOptionId: 'b',
        explanation:
          'A price of $0.35 means the market estimates about a 35% probability. This makes Candidate X an underdog—the market believes there\'s a 65% chance they will NOT win.',
      },
    },
  ],
}
